import React, { useState, useEffect } from 'react';
import { Pagination as BSPagination } from 'react-bootstrap';
import './Pagination.scss';

const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pages = [];
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Set the number of visible pages based on screen width
  const maxVisibleNumberedPages = windowWidth < 768 ? 5 : 7;
  const isDesktop = windowWidth >= 768;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (totalPages <= 1) {
    return null;
  }

  // Conditionally render "First" button for desktop
  if (isDesktop) {
    pages.push(
      <BSPagination.First key="first" onClick={() => onPageChange(1)} disabled={currentPage === 1} />
    );
  }

  // "Previous" button is always shown
  pages.push(
    <BSPagination.Prev key="prev" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
  );

  // Logic for numbered page buttons and ellipses
  if (totalPages <= maxVisibleNumberedPages + 2) {
    for (let number = 1; number <= totalPages; number++) {
      pages.push(
        <BSPagination.Item key={number} active={number === currentPage} onClick={() => onPageChange(number)}>
          {number}
        </BSPagination.Item>
      );
    }
  } else {
    // Always show the first page
    pages.push(
      <BSPagination.Item key={1} active={1 === currentPage} onClick={() => onPageChange(1)}>
        1
      </BSPagination.Item>
    );

    const pagesToShowAroundCurrent = maxVisibleNumberedPages - 2;
    let startMiddle = currentPage - Math.floor(pagesToShowAroundCurrent / 2);
    let endMiddle = currentPage + Math.floor(pagesToShowAroundCurrent / 2);

    if (startMiddle <= 1) {
      startMiddle = 2;
      endMiddle = maxVisibleNumberedPages;
    } else if (endMiddle >= totalPages) {
      startMiddle = totalPages - maxVisibleNumberedPages + 1;
      endMiddle = totalPages - 1;
    }

    if (startMiddle > 2) {
      pages.push(
        <BSPagination.Ellipsis
          key="ellipsis-start"
          onClick={() => onPageChange(Math.max(1, currentPage - 10))}
        />
      );
    }

    for (let number = startMiddle; number <= endMiddle; number++) {
      pages.push(
        <BSPagination.Item key={number} active={number === currentPage} onClick={() => onPageChange(number)}>
          {number}
        </BSPagination.Item>
      );
    }

    if (endMiddle < totalPages - 1) {
      pages.push(
        <BSPagination.Ellipsis
          key="ellipsis-end"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 10))}
        />
      );
    }

    pages.push(
      <BSPagination.Item key={totalPages} active={totalPages === currentPage} onClick={() => onPageChange(totalPages)}>
        {totalPages}
      </BSPagination.Item>
    );
  }

  // "Next" button is always shown
  pages.push(
    <BSPagination.Next key="next" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} />
  );

  // Conditionally render "Last" button for desktop
  if (isDesktop) {
    pages.push(
      <BSPagination.Last key="last" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} />
    );
  }

  return (
    <div className="pagination-container">
      <BSPagination>{pages}</BSPagination>
    </div>
  );
};

export default Pagination;